import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api, { apiError } from "@/lib/api";
import { inr } from "@/lib/format";
import { useTheme } from "@/context/ThemeContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Zap, Copy, Loader2, CheckCircle2, Clock, ShieldCheck, Smartphone, XCircle,
} from "lucide-react";
import { toast } from "sonner";

const APPS = [
  { key: "gpay", label: "Google Pay", color: "#1A73E8", pkg: "com.google.android.apps.nbu.paisa.user", tag: "Best" },
  { key: "phonepe", label: "PhonePe", color: "#5F259F", pkg: "com.phonepe.app", tag: "Prompt" },
  { key: "paytm", label: "Paytm", color: "#00BAF2", pkg: "net.one97.paytm", tag: "Prompt" },
  { key: "bhim", label: "BHIM / Any UPI", color: "#00A651", pkg: null, tag: "Best" },
];

const isAndroid = typeof navigator !== "undefined" && /android/i.test(navigator.userAgent);

// Build a reliable deep link. On Android we use an `intent://` URL with the
// app's package so the specific app opens directly (and falls back to the
// UPI chooser if not installed). On iOS / other, we use the standard
// `upi://pay?` intent which the OS resolves via the app chooser.
const appLink = (upi, app) => {
  const params = (upi || "").split("?")[1];
  if (!params) return upi || "";
  if (app.pkg && isAndroid) {
    return `intent://pay?${params}#Intent;scheme=upi;package=${app.pkg};end`;
  }
  return `upi://pay?${params}`;
};

const PRESETS = [100, 500, 1000, 2000];

export default function Pay() {
  const { orderId } = useParams();
  const { theme } = useTheme();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("pending");
  const [utr, setUtr] = useState("");
  const [amtInput, setAmtInput] = useState("");
  const [settingAmt, setSettingAmt] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get(`/public/orders/${orderId}`)
      .then((r) => { setOrder(r.data); setStatus(r.data.status); })
      .catch(() => setOrder(false));
  }, [orderId]);

  useEffect(() => {
    if (!order || ["success", "failed", "expired"].includes(status)) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/public/orders/${orderId}/status`);
        if (data.status !== status) setStatus(data.status);
        if (["success", "failed", "expired"].includes(data.status)) clearInterval(pollRef.current);
      } catch (e) { /* transient status-poll error; retry next tick */ }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [order, status, orderId]);

  const setOrderAmount = async () => {
    const amt = parseFloat(amtInput);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setSettingAmt(true);
    try {
      const { data } = await api.post(`/public/orders/${orderId}/set-amount`, { amount: amt });
      setOrder(data);
      setStatus(data.status);
      toast.success(`Pay ${inr(data.amount)} — amount locked`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSettingAmt(false);
    }
  };

  const submitUtr = async () => {    if (!/^\d{12}$/.test(utr)) { toast.error("Enter the 12-digit UTR / reference number"); return; }
    setSubmitting(true);
    try {
      await api.post(`/public/orders/${orderId}/utr`, { utr });
      setStatus("utr_submitted");
      toast.success("UTR submitted — awaiting confirmation");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (order === null)
    return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (order === false)
    return (
      <div className="min-h-screen grid place-items-center bg-background grain-overlay px-4">
        <Card className="p-8 text-center max-w-sm">
          <XCircle className="h-10 w-10 mx-auto text-destructive" />
          <h1 className="mt-3 font-heading text-xl font-bold">Order not found</h1>
          <p className="text-sm text-muted-foreground mt-1">This payment link is invalid or has been removed.</p>
        </Card>
      </div>
    );

  const done = status === "success";
  const terminal = ["failed", "expired"].includes(status);
  const needsAmount = order.open_amount && !order.upi_string;

  return (
    <div className="min-h-screen bg-background grain-overlay">
      <header className="border-b bg-card/60 glass">
        <div className="mx-auto max-w-md flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary grid place-items-center"><Zap className="h-4 w-4 text-primary-foreground" fill="currentColor" /></div>
            <span className="font-heading font-bold">WoltPe</span>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <div className="mx-auto max-w-md px-4 py-6">
        <Card className="overflow-hidden animate-fade-up">
          <div className="bg-primary/[0.06] border-b p-5 text-center">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Paying to</p>
            <p className="mt-1 font-heading text-lg font-bold">{order.merchant_name || order.payee_name}</p>
            <p className="mt-3 font-heading font-extrabold tracking-tight">
              {needsAmount
                ? <span className="text-4xl">Enter amount</span>
                : order.amount_in_app
                ? <span className="text-xl sm:text-2xl">Enter amount in your UPI app</span>
                : <span className="text-4xl">{inr(order.amount)}</span>}
            </p>
            <p className="mt-1 text-xs text-muted-foreground font-mono">Order {order.order_id}</p>
          </div>

          {done ? (
            <div className="p-8 text-center" data-testid="pay-success">
              <div className="mx-auto h-16 w-16 rounded-full bg-success/15 grid place-items-center pulse-ring">
                <CheckCircle2 className="h-9 w-9 text-success" />
              </div>
              <h2 className="mt-4 font-heading text-xl font-bold">Payment successful</h2>
              <p className="text-sm text-muted-foreground mt-1">{inr(order.amount)} received by {order.merchant_name}.</p>
            </div>
          ) : terminal ? (
            <div className="p-8 text-center" data-testid="pay-terminal">
              <XCircle className="h-12 w-12 mx-auto text-muted-foreground" />
              <h2 className="mt-3 font-heading text-lg font-bold capitalize">Order {status}</h2>
              <p className="text-sm text-muted-foreground mt-1">This payment link is no longer active.</p>
            </div>
          ) : needsAmount ? (
            <div className="p-5 space-y-4" data-testid="pay-amount-entry">
              <div className="space-y-2">
                <Label htmlFor="pay-amt">Enter the amount you want to pay (₹)</Label>
                <Input id="pay-amt" type="number" min="1" step="0.01" value={amtInput}
                  onChange={(e) => setAmtInput(e.target.value)} placeholder="e.g. 500"
                  className="font-mono text-lg" inputMode="decimal" data-testid="pay-amount-input"
                  onKeyDown={(e) => { if (e.key === "Enter") setOrderAmount(); }} />
                <div className="flex flex-wrap gap-2" data-testid="pay-amount-presets">
                  {PRESETS.map((p) => (
                    <button type="button" key={p} onClick={() => setAmtInput(String(p))}
                      data-testid={`pay-preset-${p}`}
                      className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${String(p) === amtInput ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted"}`}>
                      ₹{p.toLocaleString("en-IN")}
                    </button>
                  ))}
                </div>
              </div>
              <Button className="w-full" onClick={setOrderAmount} disabled={settingAmt} data-testid="pay-amount-continue">
                {settingAmt ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue to pay"}
              </Button>
              <p className="text-[11px] text-center text-muted-foreground">
                Pay the exact amount shown, then enter your UPI reference (UTR) below for review.
              </p>
            </div>
          ) : (
            <div className="p-5 space-y-5">
              <div className="flex flex-col items-center gap-3">
                <div className="rounded-xl bg-white p-3 border">
                  <QRCodeSVG value={order.upi_string} size={180} data-testid="pay-qr" />
                </div>
                <p className="text-xs text-muted-foreground">Scan with any UPI app, or tap below</p>
              </div>

              <p className="text-xs text-muted-foreground">Payment apps may restrict links for some merchants. If a payment is declined, use an approved payment method or contact the merchant. Check the recipient name before paying.</p>

              <div className="grid grid-cols-2 gap-2">
                {APPS.map((a) => (
                  <a key={a.key} href={appLink(order.upi_string, a)} data-testid={`pay-app-${a.key}`}>
                    <Button variant="outline" className="w-full justify-start gap-2 relative" style={{ borderColor: `${a.color}40` }}>
                      <Smartphone className="h-4 w-4" style={{ color: a.color }} />
                      <span className="truncate">{a.label}</span>
                      {a.tag === "Best" && (
                        <span className="ml-auto text-[9px] font-semibold uppercase tracking-wide text-success">Best</span>
                      )}
                      {a.tag === "Prompt" && (
                        <span className="ml-auto text-[9px] font-semibold uppercase tracking-wide text-amber-600">Prompt</span>
                      )}
                    </Button>
                  </a>
                ))}
              </div>

              <p className="text-center text-[11px] text-muted-foreground">
                Most reliable: <b>scan the QR</b> or copy the UPI ID below and pay the <b>exact amount</b> in any UPI app.
              </p>

              <div className="flex items-center gap-2 rounded-lg border bg-primary/5 border-primary/20 px-3 py-2">
                <span className="text-xs text-muted-foreground shrink-0">UPI ID</span>
                <span className="font-mono text-sm truncate flex-1">{order.vpa}</span>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { navigator.clipboard.writeText(order.vpa); toast.success("Copied"); }}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>

              <details className="rounded-lg border bg-muted/30 px-3 py-2 text-xs" data-testid="pay-help">
                <summary className="cursor-pointer font-medium text-foreground">Payment declined or didn't go through?</summary>
                <ul className="mt-2 list-disc space-y-1 pl-4 text-muted-foreground">
                  <li>Or <b>scan the QR</b> code above with any UPI app.</li>
                  <li>Or copy the UPI ID above and pay the exact amount manually.</li>
                </ul>
              </details>

              <div className="rounded-lg border border-dashed p-3 flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-4 w-4 shrink-0" />
                {status === "utr_submitted"
                  ? "UTR received — the merchant is confirming your payment."
                  : "Pay the exact amount shown, then submit your UPI reference (UTR) for review."}
              </div>

              <div className="space-y-2">
                <Label htmlFor="utr">Already paid? Enter UTR / reference no.</Label>
                <div className="flex gap-2">
                  <Input id="utr" value={utr} onChange={(e) => setUtr(e.target.value.replace(/\D/g, "").slice(0, 12))}
                    placeholder="12-digit UTR" className="font-mono" data-testid="pay-utr-input" inputMode="numeric" />
                  <Button onClick={submitUtr} disabled={submitting} data-testid="pay-utr-submit">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}
                  </Button>
                </div>
              </div>

            </div>
          )}
        </Card>

        <p className="mt-4 text-center text-xs text-muted-foreground font-mono">Secured by WoltPe · TLS encrypted</p>
      </div>
    </div>
  );
}
